import { Button, Stack, styled, Typography, TypographyProps } from '@mui/material';
import { StyledParams } from 'types/common';
import React, { useCallback, useState, useEffect, useRef } from 'react';
import { getDetailFilesChat } from 'services/files';
import { TNote } from 'types/user';
import dayjs from 'dayjs';
import { renderWithLinks } from '../../Admin/CommunicationPage/components/ChatBoxTab/ChatBox';
import { InboxForm } from '../../Admin/FilesPage/components/FileForm/components/InboxForm';
const size = 15;
type ListInboxProps = {
  fileId: number;
  titleStyled?: TypographyProps;
};
export const ListBoxMemo = ({ titleStyled, fileId }: ListInboxProps) => {
  const [listChat, setListChat] = useState<TNote[]>([]);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleGetDetailChatInterval = useCallback(
    async (page = 1) => {
      if (!fileId) return;
      const params = {
        page,
        size
      };
      const data = await getDetailFilesChat(params, Number(fileId));
      setListChat(data?.notes);
      setHasMore(data?.notes?.length < data.total);
      if (page === 1 && containerRef.current) {
        setTimeout(() => {
          containerRef.current!.scrollTop = containerRef.current!.scrollHeight;
        }, 0);
      }
    },
    [fileId]
  );

  const fetchMoreChat = useCallback(async () => {
    setIsLoading(true);
    try {
      const nextPage = page + 1;
      const params = { page: nextPage, size };
      const data = await getDetailFilesChat(params, Number(fileId));
      if (data?.notes?.length) {
        setListChat((prev) => [...data.notes, ...prev]);
        setPage(nextPage);
        setHasMore(data?.notes?.length * size < data.total);
      } else {
        setHasMore(false);
      }
    } finally {
      setIsLoading(false);
    }
  }, [fileId, page]);

  useEffect(() => {
    handleGetDetailChatInterval(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleGetDetailChatInterval]);

  return (
    <Stack style={{ marginBottom: '30px' }}>
      <WrapperHeaderBox>
        <TitleLead {...titleStyled}>Inbox</TitleLead>
      </WrapperHeaderBox>
      <WrapperContentNote>
        <TextNote>Summary Log</TextNote>
        <Stack ref={containerRef} sx={{ maxHeight: 400, overflowY: 'auto' }} gap={1.5}>
          {isLoading && (
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Loading...
            </Typography>
          )}
          {hasMore && (
            <Stack style={{ alignItems: 'center', justifyContent: 'center' }}>
              <Button
                style={{ border: '1px solid #ccc', height: '50px', width: '140px' }}
                onClick={fetchMoreChat}>
                <p>Load more...</p>
              </Button>
            </Stack>
          )}
          {listChat
            ?.sort((a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime())
            ?.map((note, index) => (
              <Stack
                key={index}
                style={{ flexDirection: 'row', gap: '10px', alignItems: 'center' }}>
                {['ADMIN', 'ROOT_ADMIN'].includes(note?.user?.role) ? (
                  <Typography fontWeight="500" variant="body2" color="text.secondary">
                    Admin - {dayjs(note.createdAt).format('YYYY/MM/DD HH:mm')}
                  </Typography>
                ) : (
                  <Typography fontWeight="500" variant="body2" color="text.secondary">
                    {`${note.user.firstName} ${note.user.lastName}`} -{' '}
                    {dayjs(note.createdAt).format('YYYY/MM/DD HH:mm')}
                  </Typography>
                )}
                <Typography
                  sx={{
                    whiteSpace: 'break-spaces',
                    wordBreak: 'break-word'
                  }}
                  fontWeight="500"
                  variant="body1"
                  key={note.id}>
                  {renderWithLinks(note.content)}
                </Typography>
              </Stack>
            ))}
        </Stack>
        <InboxForm
          reload={async () => {
            setPage(1);
            await handleGetDetailChatInterval(1);
          }}
          fileId={fileId}
        />
      </WrapperContentNote>
    </Stack>
  );
};

const WrapperContentNote = styled(Stack)(({ theme }: StyledParams) => ({
  background: theme.colors.blue,
  borderRadius: '5px',
  width: '100%',
  padding: '8px 10px',
  gap: '10px',
  maxHeight: '500px'
}));
const TextNote = styled(Typography)(() => ({
  color: '#565656',
  fontSize: 14,
  fontWeight: 500,
  lineHeight: '100%',
  whiteSpace: 'pre-line'
}));

const WrapperHeaderBox = styled(Stack)(() => ({
  width: '100%',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingBottom: '10px'
}));
const TitleLead = styled(Typography)(({ theme }: StyledParams) => ({
  color: theme.colors.light1,
  fontSize: 18,
  fontWeight: 500,
  lineHeight: '100%'
}));
